import {
  Index,
  Configure,
  useInstantSearch,
  useHits,
  usePagination
} from 'react-instantsearch'
import { SimpleGrid, Spacer, Stack } from '@backyard-ui/core'
import { useEffect, useRef, useState } from 'react'

import { setInsightsInfo } from 'scripts/react-components/entry-key/utils/analytics'
import { useTabContext } from 'scripts/react-components/entry-key/contexts/TabContext'

import { useUserResources } from 'shared/hooks/useUserResources'
import { NewProductThumb } from 'shared/components/NewProductThumb'
import { Pagination } from 'shared/components/Pagination'

import { useServerContext } from '../../../routes/routes.context'
import { GridContent } from '../../../pages/entry/components/GridContent'
import { TabSkeleton } from './TabSkeleton'
import { normalize } from '../../Content/Content.utils'
import { GRANDE_SAO_PAULO_REGION } from '../../../pages/entry/constants'
import { NoServicesFound } from './NoServicesFound'
import { IS_ON_MOBILE_APP } from '../../../constants'
import { onProductClickDataLayer } from '../../Content/Content.tracker'

/**
 * O ServiceTabContent controla quando a tab de Serviços pode processar queries e disparar insights.
 *
 * Regra de negócio: toda nova busca sempre começa pela aba de Produtos.
 * Se o usuário está em Serviços e pesquisa um novo termo, a tab de Produtos
 * é ativada primeiro. A tab de Serviços fica congelada — não processa a query
 * nem dispara insights — até que o usuário clique nela explicitamente.
 *
 * A trava central é o `shouldRenderConfigure`: o <Configure /> só renderiza
 * quando a tab de serviços é a ativa E a query é a esperada.
 *
 * Refs:
 * - hasMountedRef: evita renderizar antes da primeira visita à tab
 * - expectedQueryRef: armazena a query que serviços tem "permissão" para processar
 * - wasServiceTabRef: detecta o momento exato da transição entre tabs
 */

const HITS_PER_PAGE = 36
const MAX_VALUES_PER_FACET = 1000
const IS_CATEGORY = false

interface ServicesTabContentInnerProps {
  expectedQuery: string
  serviceIndexName?: string
  isActive: boolean
}

const ServicesTabContentInner = ({
  expectedQuery,
  serviceIndexName,
  isActive
}: ServicesTabContentInnerProps) => {
  const [showEmptyState, setShowEmptyState] = useState(false)

  const { status, results } = useInstantSearch()
  const { nbPages, currentRefinement, refine } = usePagination()
  const { userResources } = useUserResources()
  const { items, sendEvent } = useHits({ escapeHTML: false })

  const showRetailMedia = window.env.newTail.enabled
  const hasReceivedResponse = results && typeof results.nbHits === 'number'
  const hasResults = results?.nbHits && results.nbHits > 0
  const isEmpty = hasReceivedResponse && (!hasResults || items.length === 0)
  const isLoading = status === 'loading' || status === 'stalled'
  const isWaitingForResults = (isLoading && !hasResults) || items.length === 0
  const region = userResources?.region || GRANDE_SAO_PAULO_REGION

  useEffect(() => {
    if (currentRefinement > 0) {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [currentRefinement])

  useEffect(() => {
    if (!isEmpty) {
      setShowEmptyState(false)
      return
    }

    const timer = setTimeout(() => setShowEmptyState(true), 500)
    return () => clearTimeout(timer)
  }, [isEmpty])

  const setPage = (page: number) => {
    refine(page - 1)
  }

  const onServiceClick = (service: unknown, index: number) => {
    if (!isActive) return

    setInsightsInfo(service, region, serviceIndexName)
    sendEvent('click', service, 'serviceListClick')
    onProductClickDataLayer({
      index,
      region,
      product: service,
      page: currentRefinement + 1,
      term: expectedQuery ?? '',
      inWishlist: false
    })
  }

  if (showEmptyState) {
    return <NoServicesFound />
  }

  if (isWaitingForResults) {
    return <TabSkeleton />
  }

  const services = items.map(item => {
    const normalized = normalize(item, region, IS_CATEGORY)
    return {
      ...item,
      ...normalized,
      id: Number(normalized.id || item.id || item.objectID)
    }
  })

  return (
    <GridContent
      showRetailMedia={showRetailMedia}
      headerProps={{ contentType: 'services' }}>
      <Stack align="center" direction="column">
        <SimpleGrid
          columns={{
            initial: 2,
            sm: 3,
            md: 4
          }}>
          {services.map((service, index) => (
            <NewProductThumb
              key={service.objectID}
              partnerName="algolia"
              onClick={() => onServiceClick(service, index)}
              product={service}
              fullWidth
              index={index}
              itemList={`entry-key-${expectedQuery}`}
            />
          ))}
        </SimpleGrid>

        {nbPages > 1 && (
          <Pagination
            currentPage={currentRefinement + 1}
            totalPages={nbPages}
            setPage={setPage}
            pageCount={nbPages}
            page={currentRefinement + 1}
          />
        )}

        {IS_ON_MOBILE_APP && (
          <Spacer
            axis="vertical"
            size="10"
            data-testid="mobile-context-bottom-spacer"
          />
        )}
      </Stack>
    </GridContent>
  )
}

const ServiceTabContent = () => {
  const { algolia } = useServerContext()
  const { userResources } = useUserResources()
  const { indexUiState } = useInstantSearch()
  const { activeTab } = useTabContext()
  const isServiceTab = activeTab === 'services'

  const hasMountedRef = useRef(false)
  const expectedQueryRef = useRef(indexUiState?.query || '')
  const wasServiceTabRef = useRef(false)

  if (isServiceTab) {
    hasMountedRef.current = true
  }

  const defaultFilter = userResources?.region
    ? `regionalAttributes.${userResources.region}.promotionalPrice > 0 AND regionalAttributes.${userResources.region}.available=1 AND regionalAttributes.${userResources.region}.stock.hasStock=1`
    : ''

  const searchQuery = indexUiState?.query || ''

  /**
   * Detecta se a tab de serviços acabou de ser ativada (transição produtos → serviços).
   * Quando isso ocorre, sincroniza a query esperada com a query atual
   * para que o <Configure /> renderize imediatamente com os parâmetros corretos.
   */
  const justActivated = isServiceTab && !wasServiceTabRef.current

  if (justActivated) {
    expectedQueryRef.current = searchQuery
  }

  /**
   * wasServiceTabRef: salva o estado atual da tab para que o
   * justActivated detecte quando o usuário troca de Produtos para Serviços.
   *
   * expectedQueryRef: quando a tab de Serviços não está ativa,
   * atualiza a query esperada para que esteja pronta quando o usuário clicar nela.
   */
  useEffect(() => {
    wasServiceTabRef.current = isServiceTab

    if (!isServiceTab) {
      expectedQueryRef.current = searchQuery
    }
  }, [isServiceTab, searchQuery])

  if (!userResources?.region || !hasMountedRef.current) return null

  /**
   * Guarda contra a corrida de estados.
   * Quando a query muda enquanto a tab de serviços ainda está ativa
   * (antes do useEffect do SearchResultTabs trocar para Produtos),
   * o expectedQueryRef ainda tem a query antiga. A comparação impede
   * que o <Configure /> renderize nesse instante, evitando que
   * insights de serviços disparem com a query nova no momento errado.
   */
  const shouldRenderConfigure =
    isServiceTab && expectedQueryRef.current === searchQuery

  return (
    <Index indexName={algolia?.serviceIndexName} EXPERIMENTAL_isolated>
      {shouldRenderConfigure && (
        <Configure
          facets={['*']}
          filters={defaultFilter}
          hitsPerPage={HITS_PER_PAGE}
          query={searchQuery}
          analytics
          clickAnalytics
          maxValuesPerFacet={MAX_VALUES_PER_FACET}
          page={0}
          facetingAfterDistinct
        />
      )}

      <ServicesTabContentInner
        expectedQuery={searchQuery}
        serviceIndexName={algolia?.serviceIndexName}
        isActive={isServiceTab}
      />
    </Index>
  )
}

export default ServiceTabContent
